import { useEffect } from 'react'
import { onlineManager } from '@tanstack/react-query'
import { readState } from '@api/v1/readState'

/**
 * `readState` plus a PERMANENT diagnostic for its worst state.
 *
 * The rule this exists for: *"a fail-soft path you cannot observe is
 * indistinguishable from a fix"* (CLAUDE.md; CC-49899 bounced three times behind
 * a swallowing catch). The `'blocked'` state is the extreme case of that — a
 * paused react-query fetch produces **no request, no error and no log**, so the
 * failing load leaves no trace anywhere, on the client OR the server, by
 * construction. That is exactly why an intermittent "your firm has no reports"
 * survived two reports from Jordan and several clean server-side investigations.
 *
 * The mechanism was proved (see `src/pages/__tests__/Reports.paused.test.jsx`),
 * but the TRIGGER was not: `onlineManager` starts online and only flips on the
 * window `offline` event — it never reads `navigator.onLine` — so a failing load
 * needs a genuine offline event inside the few hundred ms between the
 * QueryClient mounting and the fetch starting, and then no `online` event for
 * 30+ seconds. Nobody has captured that. So this logs the three facts that would
 * settle it, the next time it happens to anyone.
 *
 * **Do not remove this warning** to quieten the console. Per the CC-49899 rule,
 * diagnostics added for this class of defect are permanent.
 *
 * It lives in the hook rather than in `readState()` itself for two reasons: a
 * pure function called on every render must not log, and putting the instrument
 * where the primitive is *consumed* means every page that adopts `readState`
 * during the 30-file sweep (spec 087 §4.6) gets the instrument for free rather
 * than having to remember it.
 *
 * @param {string} label  Which read this is, e.g. 'reports-list'. Appears in the log.
 * @param {object} query  A `useQuery` result.
 * @returns {'failed'|'blocked'|'idle'|'loading'|'ready'}
 */
export const useReadState = (label, query) => {
  const state = readState(query)

  useEffect(() => {
    if (state !== 'blocked') return
    // eslint-disable-next-line no-console
    console.warn(
      `[readState] "${label}" is BLOCKED — react-query paused the fetch, so no ` +
        'request was sent and this screen is NOT describing your data.',
      {
        navigatorOnLine: typeof navigator === 'undefined' ? null : navigator.onLine,
        onlineManagerIsOnline: onlineManager.isOnline(),
        fetchStatus: query?.fetchStatus ?? null,
      }
    )
    // `query` is intentionally not a dep: it is a new object every render, and the
    // point is one log per transition INTO blocked, not one per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, label])

  return state
}

export default useReadState
